import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, LogOut, User, Shield, Award, Search, ShoppingBag, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import axios from 'axios';
import { API } from '@/App';
import GlobalSearch from '@/components/GlobalSearch';
import NotificationsDropdown from '@/components/NotificationsDropdown';
import LiveScoreTicker from '@/components/LiveScoreTicker';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/f617a5a9-dda7-40dc-bbb9-de762658adaf/images/5d32d876981551b4c98ceec76bfdad1e26bc99093c09f2a5f2242b603b114d79.png";

export const Navbar = ({ user, setUser, transparent = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const publicLinks = [
    { name: 'Home', path: '/' },
    { name: 'Leagues', path: '/leagues' },
    { name: 'Live', path: '/live' },
  ];

  const quickLinks = [
    { 
      name: 'Merchandise', 
      path: 'https://www.starsportsus.net/', 
      icon: ShoppingBag,
      external: true,
      color: 'text-amber-600'
    },
    { 
      name: 'Lessons', 
      path: '/lessons', 
      icon: GraduationCap,
      external: false,
      color: 'text-teal-600'
    },
  ];

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      console.error('Logout error:', e);
    }
    setUser(null);
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return null;
    if (user.role === 'admin') return '/admin';
    if (user.role === 'scorer') return '/scorer';
    return null;
  };

  const dashboardLink = getDashboardLink();

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${transparent ? 'bg-transparent' : 'bg-white/80 backdrop-blur-xl border-b border-slate-200'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="CricORama" className="w-10 h-10 object-contain" />
            <span className={`font-bold text-xl font-display ${transparent ? 'text-white' : 'text-slate-900'}`}>CricORama</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            {publicLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? transparent ? 'text-white' : 'text-blue-600'
                    : transparent ? 'text-white/70 hover:text-white' : 'text-slate-600 hover:text-blue-600'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* Quick Links - Merchandise & Lessons */}
            {quickLinks.map((link) => {
              const Icon = link.icon;
              if (link.external) {
                return (
                  <a
                    key={link.name}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-sm font-medium transition-colors flex items-center gap-1 ${
                      transparent ? 'text-white/70 hover:text-white' : `${link.color} hover:opacity-80`
                    }`}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden lg:inline">{link.name}</span>
                  </a>
                );
              }
              return (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-sm font-medium transition-colors flex items-center gap-1 ${
                    location.pathname === link.path
                      ? transparent ? 'text-white' : link.color
                      : transparent ? 'text-white/70 hover:text-white' : `text-slate-600 hover:${link.color}`
                  }`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden lg:inline">{link.name}</span>
                </Link>
              );
            })}

            {dashboardLink && (
              <Link
                to={dashboardLink}
                className={`text-sm font-medium transition-colors flex items-center gap-1 ${
                  location.pathname.startsWith(dashboardLink)
                    ? transparent ? 'text-white' : 'text-blue-600'
                    : transparent ? 'text-white/70 hover:text-white' : 'text-slate-600 hover:text-blue-600'
                }`}
              >
                {user.role === 'admin' ? <Shield className="w-4 h-4" /> : <Award className="w-4 h-4" />}
                Dashboard
              </Link>
            )}

            {/* Live Score Ticker */}
            <LiveScoreTicker transparent={transparent} />
          </div>

          {/* User Menu / Login */}
          <div className="flex items-center gap-2">
            {/* Search Button */}
            <button
              onClick={() => setSearchOpen(true)}
              className={`p-2 rounded-full transition-colors ${
                transparent 
                  ? 'hover:bg-white/10 text-white' 
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
              data-testid="search-btn"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Notifications Bell */}
            <NotificationsDropdown user={user} transparent={transparent} />

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 p-1 pr-2" data-testid="user-menu-trigger">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user.profile_image_url} alt={user.name} />
                      <AvatarFallback className="bg-blue-600 text-white text-sm">
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className={`hidden sm:inline text-sm font-medium ${transparent ? 'text-white' : 'text-slate-700'}`}>
                      {user.name?.split(' ')[0]}
                    </span>
                    <ChevronDown className={`w-4 h-4 ${transparent ? 'text-white/70' : 'text-slate-400'}`} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                    <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                      user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                      user.role === 'scorer' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </span>
                  </div>
                  <DropdownMenuSeparator />
                  {dashboardLink && (
                    <DropdownMenuItem asChild>
                      <Link to={dashboardLink} className="flex items-center gap-2 cursor-pointer">
                        <User className="w-4 h-4" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button
                  className={`rounded-full px-6 ${
                    transparent 
                      ? 'bg-white text-blue-600 hover:bg-white/90' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
              </Link>
            )}

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className={`w-6 h-6 ${transparent ? 'text-white' : 'text-slate-600'}`} />
              ) : (
                <Menu className={`w-6 h-6 ${transparent ? 'text-white' : 'text-slate-600'}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200 bg-white/95 backdrop-blur-xl">
            <div className="flex flex-col gap-2">
              {publicLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg ${
                    location.pathname === link.path
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Quick Links in Mobile */}
              <div className="border-t border-slate-200 mt-2 pt-2">
                <p className="px-4 py-1 text-xs font-semibold text-slate-400 uppercase">Quick Links</p>
                {quickLinks.map((link) => {
                  const Icon = link.icon;
                  if (link.external) {
                    return (
                      <a
                        key={link.name}
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                        className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 ${link.color} hover:bg-slate-50`}
                      >
                        <Icon className="w-4 h-4" />
                        {link.name}
                      </a>
                    );
                  }
                  return (
                    <Link
                      key={link.name}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 ${
                        location.pathname === link.path
                          ? 'bg-teal-50 text-teal-600'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {link.name}
                    </Link>
                  );
                })}
              </div>

              {dashboardLink && (
                <Link
                  to={dashboardLink}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 ${
                    location.pathname.startsWith(dashboardLink)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {user?.role === 'admin' ? <Shield className="w-4 h-4" /> : <Award className="w-4 h-4" />}
                  Dashboard
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Global Search Modal */}
      {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}
    </nav>
  );
};

export default Navbar;
